import { Bell } from 'lucide-react';
import * as React from 'react';

import { InAppNotice } from './in-app-notice';
import { useMemberPushRegistration } from './push-registration';

/**
 * The push offer — asked in context, and never on load (`MEM-040` AC-3).
 *
 * ## The moment
 *
 * `MEM-040`'s Notes pick it: *"Asking after the member has seen a reason — their first match
 * release, say — converts far better and is the only chance the product gets."* So this renders
 * on the matches screens, and only once there are matches on them. A member reading a list of
 * people they have been matched with is a member for whom "we will tell you when the next ones
 * land" is an offer rather than an interruption.
 *
 * `hasReason` is the caller's, so the moment is a property of the screen rather than of this
 * component. A second caller would state its own reason; there is not one yet, and adding one
 * without a reason to go with it would be re-inventing the prompt on load.
 *
 * ## Nothing happens until the member taps
 *
 * There is no effect in this file. Rendering it asks for nothing: the browser dialogue is raised
 * inside `enable`, from the button's handler.
 * `push-is-never-asked-for-on-load.test.tsx` mounts this component — with a reason, with the
 * offer showing — and asserts `Notification.requestPermission` was not called.
 *
 * ## Declining costs nothing
 *
 * `MEM-040` AC-5. Dismiss takes the notice down and remembers the answer; Block in the browser
 * dialogue does the same by way of `permission`. Neither changes anything else on any screen, and
 * the outcome is reported in the notice itself rather than as a toast, because a toast about a
 * decision the member just made is telling them what they did.
 */
export function PushNotice({
  hasReason,
  enabled,
}: {
  /** The member is looking at something push would have told them about. */
  readonly hasReason: boolean;
  /** There is a signed-in member. A visitor has no account to register a subscription against. */
  readonly enabled: boolean;
}): React.ReactElement | null {
  const push = useMemberPushRegistration({ hasReason, enabled });
  const [outcome, setOutcome] = React.useState<'granted' | 'denied' | 'failed' | null>(null);
  const [busy, setBusy] = React.useState(false);

  if (outcome === 'granted') {
    return (
      <InAppNotice
        icon={<Bell className="h-5 w-5" />}
        title={PUSH_ENABLED_TITLE}
        body={PUSH_ENABLED_BODY}
        onDismiss={() => {
          setOutcome(null);
        }}
        dismissLabel={PUSH_DISMISS_LABEL}
        className="mb-4"
      />
    );
  }

  // A no is a no. The notice goes, and nothing replaces it — `MEM-040` AC-5 is that the member
  // who declines is in exactly the state they were in before they were asked.
  if (outcome !== null) return null;

  /*
    `PLT-008` AC-6 — the consequence stated where it applies.

    An iPhone member reading a released match set is exactly the member for whom "we can tell you
    when the next ones land" is an offer, and it is the one member `MEM-040` correctly refuses to
    make it to: Safari exposes `PushManager` in a tab and will not grant permission through it, so
    raising the dialogue there spends the single chance on a question that can only be answered
    no. What `MEM-040` left behind was silence, and silence is what makes a member conclude the
    product is broken when they turn notifications on somewhere else and hear nothing.

    So the offer's slot carries an explanation instead. **No action control**, because the action
    is in Safari's Share menu and a button that cannot press it would be a lie; and no dismissal,
    because there is nothing to decline — it is a fact about the device, and it stops being drawn
    the moment the app is on the home screen.
  */
  if (push.iosNeedsInstall) {
    return (
      <InAppNotice
        icon={<Bell className="h-5 w-5" />}
        title={PUSH_ON_IOS_TITLE}
        body={PUSH_ON_IOS_BODY}
        className="mb-4"
      />
    );
  }

  if (!push.shouldOffer) return null;

  return (
    <InAppNotice
      icon={<Bell className="h-5 w-5" />}
      title={PUSH_OFFER_TITLE}
      body={PUSH_OFFER_BODY}
      action={{
        label: PUSH_OFFER_ACTION,
        busy,
        onSelect: () => {
          setBusy(true);
          void push.enable().then((result) => {
            setBusy(false);
            setOutcome(result);
          });
        },
      }}
      onDismiss={() => {
        push.dismiss();
      }}
      dismissLabel={PUSH_DISMISS_LABEL}
      className="mb-4"
    />
  );
}

/**
 * The copy, and it is written to be answerable.
 *
 * It says what will be sent — matches and connection requests — because "enable notifications"
 * with no object is the request members refuse. It does not promise anything about frequency,
 * which `MEM-041` will own, and it does not say "important updates", which means nothing.
 */
export const PUSH_OFFER_TITLE = 'Want to know when new matches land?';
export const PUSH_OFFER_BODY =
  'We can send a notification when your matches are ready and when somebody asks to connect. ' +
  'Nothing else, and you can turn it off whenever you like.';
export const PUSH_OFFER_ACTION = 'Turn on notifications';
export const PUSH_DISMISS_LABEL = 'No thanks';

export const PUSH_ENABLED_TITLE = 'Notifications are on';
export const PUSH_ENABLED_BODY =
  'We will let you know when your matches are ready and when somebody asks to connect.';

/**
 * The iOS consequence (`PLT-008` AC-6).
 *
 * It names the limitation as Apple's rather than as ours, because a member who believes H2HSpark
 * chose this will look for the setting that turns it off. And it gives the whole instruction —
 * the Share button, the menu item — because "install the app" on iOS is a sentence members read
 * as "download it from the App Store", go looking, and do not find.
 */
export const PUSH_ON_IOS_TITLE = 'Notifications on iPhone need the app on your home screen';
export const PUSH_ON_IOS_BODY =
  'Apple only delivers notifications to web apps that have been added to the home screen. In ' +
  'Safari, tap the Share button and choose “Add to Home Screen”, and we can tell you when your ' +
  'matches are ready and when somebody asks to connect.';
